/**
 * Plates.
 *
 * DESIGN.md section 4: the plate is the identity. Players, civilians, patrol
 * units, everyone. There is no username. Being reducible to your plate is what
 * the system does to people, so the identifier is painted on the car in public
 * view and it is not a secret.
 *
 * Everything about a plate that the game needs is here: the formats, the
 * generator, and the registry that keeps them unique. Nothing here knows about
 * a human being, because nothing about a human being is collected.
 *
 * The formats are real. Vehicles in a Wisconsin county carry the plates that
 * vehicles in a Wisconsin county carry, and getting that wrong would make the
 * one identifier the whole game runs on the one thing in it that is invented.
 */

import type { Rng } from './rng.ts';

/**
 * A plate format. `pattern` uses A for a letter and 9 for a digit; every other
 * character is a literal.
 */
export interface PlateFormat {
  /** Two-letter issuing jurisdiction code. */
  abbr: string;
  /** Full jurisdiction name, shown on the read image and in query results. */
  jurisdiction: string;
  pattern: string;
  /**
   * Relative share of out-of-state traffic. Not a measurement. It is ordered
   * the way traffic in south-central Wisconsin is ordered, with Illinois first
   * because the Dells and Devil's Lake draw from Chicago, and it is a tunable.
   */
  weight: number;
}

/**
 * Letters excluded from generated plates.
 *
 * Wisconsin does not issue I, O or Q on standard passenger plates, because
 * they read as 1 and 0. That is the same confusion set the misread mechanic in
 * M3 will draw from, so it matters that the exclusion is the real one rather
 * than a designer's guess about which characters look alike.
 */
const PLATE_LETTERS = 'ABCDEFGHJKLMNPRSTUVWXYZ';
const PLATE_DIGITS = '0123456789';

/**
 * The home jurisdiction. Real, along with the county the map is baked from.
 * A bake overrides this from the county file.
 */
export const DEFAULT_HOME_STATE = { abbr: 'WI', name: 'Wisconsin' } as const;

/**
 * Wisconsin's standard passenger serial, three letters and four digits,
 * issued in this form since April 2017. Roughly 122 million combinations, so
 * the namespace is nowhere near a constraint even for a county that runs for
 * years.
 */
export const HOME_PLATE_PATTERN = 'AAA-9999';

/**
 * What a squad car carries.
 *
 * Wisconsin issues municipal plates to local-government vehicles on their own
 * series, and they do not look like a passenger plate -- which matters here for
 * a reason beyond flavour. §8's whole argument is about what can be worked out
 * from a plate, and a plate that announces the vehicle is a police car is a
 * worked example of exactly that: you learn something real about the driver
 * from six characters, before any database is involved.
 *
 * It also means a marked unit is identifiable in the record and not only on the
 * road. An officer searching the detection table can see which reads are their
 * own fleet, which is a thing an operator can do and a civilian cannot.
 */
export const MUNICIPAL_PLATE_PATTERN = 'MG-9999';

/**
 * Plates that are never issued to anybody by the registry.
 *
 * Wisconsin sells personalized plates of up to seven characters, so a serial
 * made of letters where the digits normally go is a real thing you can buy and
 * not a break in the format.
 *
 * Which is worth noticing rather than glossing: a vanity plate is *more*
 * identifying than a random one, because it is memorable and it is unique in a
 * way a sequence is not. The person who runs the archive driving one is the
 * kind of joke the game should be able to make without explaining.
 */
export const RESERVED_PLATES: readonly string[] = Object.freeze(['DRT-STAR']);

/**
 * Out-of-state formats, as those states actually issue them.
 *
 * Older series still on the road are not modeled. Every one of these is the
 * current standard passenger serial, which is what most of the traffic on a
 * Wisconsin highway is wearing.
 */
export const OUT_OF_STATE: readonly PlateFormat[] = Object.freeze([
  { abbr: 'IL', jurisdiction: 'Illinois', pattern: 'AA 99999', weight: 34 },
  { abbr: 'MN', jurisdiction: 'Minnesota', pattern: 'AAA 999', weight: 20 },
  { abbr: 'IA', jurisdiction: 'Iowa', pattern: 'AAA 999', weight: 13 },
  { abbr: 'MI', jurisdiction: 'Michigan', pattern: 'AAA 9999', weight: 10 },
  { abbr: 'IN', jurisdiction: 'Indiana', pattern: '999AAA', weight: 7 },
  { abbr: 'MO', jurisdiction: 'Missouri', pattern: 'AA9A9A', weight: 5 },
  { abbr: 'OH', jurisdiction: 'Ohio', pattern: 'AAA 9999', weight: 5 },
  { abbr: 'TX', jurisdiction: 'Texas', pattern: 'AAA 9999', weight: 3 },
  { abbr: 'FL', jurisdiction: 'Florida', pattern: 'AAA A99', weight: 3 },
]);

/**
 * Share of simulated traffic wearing an out-of-state plate.
 *
 * DESIGN.md section 4 called for *most* traffic to be out of state, on the
 * reasoning that it widens the plate namespace and keeps a home plate
 * meaningful. That reasoning was written when the home state was invented and
 * its format was scarce. It no longer holds and the number is inverted here,
 * for two reasons:
 *
 * - Wisconsin's real serial has about 122 million combinations, so there is no
 *   namespace to widen.
 * - If most traffic were out of state, a Wisconsin plate would be the rare
 *   thing on the road, and any player wearing one would be identifying
 *   themselves to enforcement by the fact of it. That is the opposite of what
 *   the design wants, which is for a player to be indistinguishable from the
 *   noise.
 *
 * A quarter is high for a rural Wisconsin county and deliberately so: this one
 * has the Dells and Devil's Lake in it, and tourist traffic is real. Tunable.
 */
export const OUT_OF_STATE_SHARE = 0.25;

/**
 * The state behind an abbreviation, said in full.
 *
 * An alert that reads "IA silver Ram" is asking the operator to know the
 * postal codes of nine states. The full name costs nothing and the Aurora
 * lesson is specifically that nobody cross-checked this field -- making it
 * legible is the least the screen can do.
 */
/**
 * Every U.S. state (and D.C.), abbreviation to full name.
 *
 * The NPC traffic upstream only ever wears the nine neighbor plates OUT_OF_STATE
 * lists, because that is what actually drives past a reader in this county. A
 * player buying a custom plate is a different thing entirely: somebody choosing
 * where their fictional car is registered, and there is no reason to stop them
 * at nine. So the store's state list, the validity check, and the name shown for
 * any abbreviation all read from the full union here, while the traffic model
 * keeps its own short, weighted list.
 */
export const US_STATES: Readonly<Record<string, string>> = Object.freeze({
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
  MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
  NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
  TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
  WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
});

/**
 * The slogan a state stamps across the bottom of its plate.
 *
 * Not decoration: the store renders the plate the buyer is about to own, and a
 * real plate carries its state's line under the serial. These are the familiar
 * ones -- the words actually on the road -- so the preview reads as the thing it
 * is standing in for rather than a mock-up of one.
 */
export const STATE_SLOGANS: Readonly<Record<string, string>> = Object.freeze({
  AL: 'Heart of Dixie', AK: 'The Last Frontier', AZ: 'Grand Canyon State',
  AR: 'The Natural State', CA: 'The Golden State', CO: 'Colorful Colorado',
  CT: 'Constitution State', DE: 'The First State',
  DC: 'Taxation Without Representation', FL: 'Sunshine State', GA: 'Peach State',
  HI: 'Aloha State', ID: 'Famous Potatoes', IL: 'Land of Lincoln',
  IN: 'The Hoosier State', IA: 'The Hawkeye State', KS: 'The Sunflower State',
  KY: 'Bluegrass State', LA: "Sportsman's Paradise", ME: 'Vacationland',
  MD: 'The Old Line State', MA: 'The Spirit of America', MI: 'Pure Michigan',
  MN: '10,000 Lakes', MS: 'Magnolia State', MO: 'Show-Me State',
  MT: 'Big Sky Country', NE: 'The Cornhusker State', NV: 'The Silver State',
  NH: 'Live Free or Die', NJ: 'Garden State', NM: 'Land of Enchantment',
  NY: 'The Empire State', NC: 'First in Flight', ND: 'Peace Garden State',
  OH: 'The Heart of It All', OK: 'Native America', OR: 'Pacific Wonderland',
  PA: 'Keystone State', RI: 'The Ocean State', SC: 'The Palmetto State',
  SD: 'Great Faces. Great Places.', TN: 'The Volunteer State',
  TX: 'The Lone Star State', UT: 'Life Elevated', VT: 'Green Mountain State',
  VA: 'Virginia is for Lovers', WA: 'Evergreen State',
  WV: 'Wild and Wonderful', WI: "America's Dairyland", WY: 'Forever West',
});

/** The slogan for a state, or an empty string if none is on file. */
export function stateSlogan(abbr: string): string {
  return STATE_SLOGANS[abbr] ?? '';
}

/** Every state a custom plate may be issued in: home first, then the rest A-Z. */
export function plateStates(): { abbr: string; name: string }[] {
  const home = DEFAULT_HOME_STATE.abbr;
  const rest = Object.entries(US_STATES)
    .filter(([abbr]) => abbr !== home)
    .map(([abbr, name]) => ({ abbr, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return [{ abbr: home, name: DEFAULT_HOME_STATE.name }, ...rest];
}

/** Whether an abbreviation is a state a plate can be issued in. */
export function isPlateState(abbr: string): boolean {
  return abbr in US_STATES;
}

export function jurisdictionName(abbr: string): string {
  return US_STATES[abbr] ?? abbr;
}

export interface Plate {
  /** The characters on the plate, uppercase, separators included. */
  text: string;
  abbr: string;
  jurisdiction: string;
}

/** Render one plate from a pattern. Does not check uniqueness. */
export function formatPlate(rng: Rng, format: PlateFormat): Plate {
  let text = '';
  for (const ch of format.pattern) {
    if (ch === 'A') text += PLATE_LETTERS[rng.int(PLATE_LETTERS.length)];
    else if (ch === '9') text += PLATE_DIGITS[rng.int(PLATE_DIGITS.length)];
    else text += ch;
  }
  return { text, abbr: format.abbr, jurisdiction: format.jurisdiction };
}

/**
 * Keeps plates unique the way plates are actually unique.
 *
 * Which is **per issuing state, not globally**, and the difference is a
 * mechanic rather than a detail.
 *
 * This registry used to key on the serial alone, reasoning that two vehicles
 * sharing a plate would make the detection table ambiguous and every
 * enforcement query would quietly return the union of two lives. Every word of
 * that is true. It is also the thing that happens, and refusing to model it
 * made the game *more* forgiving than the system it is about.
 *
 * Iowa and Minnesota both issue `AAA 999`. There are about 12.9 million such
 * serials and a few million vehicles wearing them, so the same three letters
 * and three digits sit on cars in both states right now. A plate read
 * **perfectly**, with no camera error at all, can therefore alert against an
 * entry filed by a state the car has never been in.
 *
 * DESIGN.md section 7 names that as the likeliest missing piece behind a false
 * alert rate no amount of character misreading can explain. So the serial is no
 * longer the key. `(state, serial)` is, the detection row already carries the
 * state, and a query that ignores it gets the union of two lives, correctly,
 * and says so.
 */
export class PlateRegistry {
  /** Keyed `WI:ABC-1234`. Two states may hold the same serial, as they do. */
  private readonly taken = new Set<string>();
  /**
   * Serials nobody gets, in any state. Reserved plates are held globally
   * because the point of one is that it identifies exactly one car.
   */
  private readonly reserved = new Set<string>(RESERVED_PLATES);
  private readonly home: PlateFormat;
  private readonly outOfStateWeights: number[];

  constructor(homeState: { abbr: string; name: string } = DEFAULT_HOME_STATE) {
    this.home = {
      abbr: homeState.abbr,
      jurisdiction: homeState.name,
      pattern: HOME_PLATE_PATTERN,
      weight: 1,
    };
    this.outOfStateWeights = OUT_OF_STATE.map((format) => format.weight);
  }

  get size(): number {
    return this.taken.size;
  }

  /**
   * Whether this exact plate is issued. Takes the state, because without it
   * the question has no answer: `AAA 999` is issued in Iowa and in Minnesota
   * and they are different cars.
   */
  has(abbr: string, text: string): boolean {
    return this.taken.has(`${abbr}:${text}`);
  }

  /** How many states have issued this serial. The collision, counted. */
  statesHolding(text: string, states: readonly string[]): string[] {
    return states.filter((abbr) => this.taken.has(`${abbr}:${text}`));
  }

  /** Reserve a specific plate. False when that state already issued it. */
  claim(abbr: string, text: string): boolean {
    if (this.reserved.has(text)) return false;
    const key = `${abbr}:${text}`;
    if (this.taken.has(key)) return false;
    this.taken.add(key);
    return true;
  }

  /**
   * Hold a serial so nobody is issued it, in any state.
   *
   * Used for plates that already belong to a player. Global rather than
   * per-state because a player may be wearing any state's plate and the point
   * is that their serial is theirs: two cars on one serial would make every
   * query about it return two lives, which this game models deliberately
   * elsewhere and must never do to a player by accident.
   */
  hold(text: string): void {
    this.reserved.add(text);
  }

  /** Take a reserved serial for a specific car. The one way past `reserved`. */
  claimReserved(abbr: string, text: string): boolean {
    const key = `${abbr}:${text}`;
    if (this.taken.has(key)) return false;
    this.taken.add(key);
    return true;
  }

  release(abbr: string, text: string): void {
    this.taken.delete(`${abbr}:${text}`);
  }

  /**
   * A fresh unique plate. `homeState` forces the home format; omitting it
   * draws by OUT_OF_STATE_SHARE.
   *
   * Retries on collision and then widens the pattern rather than looping
   * forever, because a saturated namespace is a real condition once a county
   * has run for years.
   */
  /**
   * A municipal plate, for a government vehicle.
   *
   * Always the home state: a county's police cars are not registered in
   * Illinois. Falls back to an ordinary plate only if the municipal series is
   * somehow exhausted, because a car with no plate at all is not a thing this
   * simulation can represent.
   */
  issueMunicipal(rng: Rng): Plate {
    const format: PlateFormat = {
      abbr: this.home.abbr,
      jurisdiction: this.home.jurisdiction,
      pattern: MUNICIPAL_PLATE_PATTERN,
      weight: 0,
    };
    for (let attempt = 0; attempt < 4096; attempt++) {
      const plate = formatPlate(rng, format);
      if (this.claim(plate.abbr, plate.text)) return plate;
    }
    return this.issue(rng, true);
  }

  issue(rng: Rng, homeState?: boolean): Plate {
    const wantHome = homeState ?? !rng.chance(OUT_OF_STATE_SHARE);
    for (let attempt = 0; attempt < 64; attempt++) {
      const format = wantHome
        ? this.home
        : (OUT_OF_STATE[rng.weighted(this.outOfStateWeights)] as PlateFormat);
      const plate = formatPlate(rng, format);
      if (this.claim(plate.abbr, plate.text)) return plate;
    }
    // Crowded for that format. Fall back to the widest pattern available so
    // issuance stays possible instead of throwing mid-simulation.
    const widest = widestFormat(this.home);
    for (let attempt = 0; attempt < 8192; attempt++) {
      const plate = formatPlate(rng, widest);
      if (this.claim(plate.abbr, plate.text)) return plate;
    }
    throw new Error('PlateRegistry: plate namespace exhausted');
  }
}

/**
 * Whether a string could have been issued under this pattern.
 *
 * Needed because a nationwide list is checked by serial, and the question "who
 * else might hold this serial" has a much smaller answer than "every state".
 * Minnesota never issued `ABC-1234`: its passenger serial is `AAA 999`, and no
 * amount of matching on six characters changes what a state actually prints.
 *
 * Getting this wrong is not a rounding error. Treating every state as a
 * possible holder of every serial makes almost every alert a cross-state false
 * hit -- measured at 88% before this existed -- which would describe a system
 * so useless that nobody would have deployed a second one.
 */
export function matchesPattern(text: string, pattern: string): boolean {
  if (text.length !== pattern.length) return false;
  for (let i = 0; i < pattern.length; i++) {
    const ch = pattern[i] as string;
    const got = text[i] as string;
    if (ch === 'A') {
      if (!PLATE_LETTERS.includes(got)) return false;
    } else if (ch === '9') {
      if (!PLATE_DIGITS.includes(got)) return false;
    } else if (ch !== got) {
      return false;
    }
  }
  return true;
}

/**
 * Which states could have issued this exact serial.
 *
 * The set a nationwide serial check can actually collide across, and usually
 * it is one. `AAA 999` is Minnesota and Iowa; `AAA 9999` is Michigan, Ohio and
 * Texas; Wisconsin's `AAA-9999` is Wisconsin alone, separator included.
 */
export function statesIssuing(
  text: string,
  homeState: { abbr: string; name: string } = DEFAULT_HOME_STATE,
): string[] {
  const found: string[] = [];
  if (matchesPattern(text, HOME_PLATE_PATTERN)) found.push(homeState.abbr);
  for (const format of OUT_OF_STATE) {
    if (matchesPattern(text, format.pattern)) found.push(format.abbr);
  }
  return found;
}

/** How many distinct serials a pattern can produce. */
export function patternSpace(pattern: string): number {
  let space = 1;
  for (const ch of pattern) {
    if (ch === 'A') space *= PLATE_LETTERS.length;
    else if (ch === '9') space *= PLATE_DIGITS.length;
  }
  return space;
}

function widestFormat(home: PlateFormat): PlateFormat {
  let best = home;
  let bestSpace = patternSpace(home.pattern);
  for (const format of OUT_OF_STATE) {
    const space = patternSpace(format.pattern);
    if (space > bestSpace) {
      bestSpace = space;
      best = format;
    }
  }
  return best;
}


/**
 * What the car looks like.
 *
 * DESIGN.md section 3 already specified this: a detection row is
 * `{plate, camera_id, timestamp, direction, make, model, color}`. It is here
 * because of what happened in Aurora, Colorado in 2020.
 *
 * A mother and four children, aged six to seventeen, were held face down on
 * the pavement at gunpoint. Her Colorado plate carried the same numbers as a
 * stolen **motorcycle with Montana plates**. The reporting is specific about
 * where that went wrong: the officers did not cross reference the plate against
 * the state or the make, either of which would have ended it before anybody got
 * out of a car.
 *
 * So this is not a hole in the vendor's matching. The record held the state and
 * it held the make. Somebody did not look, which is the Institute for Justice
 * finding in section 1 rendered as a mechanic: about a third machine error and
 * **two thirds human error**.
 *
 * Which means the game must give the player everything they need to check, and
 * then let them not check.
 */

export type VehicleKindOfCar = 'car' | 'suv' | 'pickup' | 'van' | 'motorcycle';

export interface Description {
  make: string;
  color: string;
  body: VehicleKindOfCar;
}

/**
 * Who builds what.
 *
 * Make and body were drawn independently, and the first live alert the game
 * ever fired read "wanted: WI white Subaru motorcycle". Subaru does not build
 * motorcycles.
 *
 * That is not a cosmetic slip, it is the mechanic failing. The Aurora field
 * only works if the description on the screen is one a person would believe:
 * the player is supposed to look at a record, look at the car in front of them,
 * and notice they cannot be the same vehicle. A record that is transparently
 * junk teaches the opposite lesson, which is that the field is noise and there
 * is no point reading it.
 *
 * So the body comes first and the make comes from what builds it. Weighted the
 * way a Wisconsin county looks: mostly trucks and crossovers, motorcycles rare.
 */
const BODY_MAKES: Readonly<Record<VehicleKindOfCar, readonly string[]>> = Object.freeze({
  car: ['Chevrolet', 'Ford', 'Toyota', 'Honda', 'Subaru', 'Nissan', 'Dodge', 'Hyundai',
        'Kia', 'Buick', 'Chrysler', 'Volkswagen'],
  suv: ['Chevrolet', 'Ford', 'Toyota', 'Honda', 'Jeep', 'Subaru', 'Nissan', 'Dodge',
        'GMC', 'Hyundai', 'Kia', 'Buick', 'Volkswagen'],
  // Wisconsin. The list is short because the market is.
  pickup: ['Ford', 'Chevrolet', 'RAM', 'GMC', 'Toyota', 'Nissan'],
  van: ['Chrysler', 'Dodge', 'Honda', 'Toyota', 'Kia', 'Ford', 'Chevrolet'],
  motorcycle: ['Harley-Davidson', 'Honda', 'Yamaha', 'Kawasaki', 'Suzuki', 'Indian',
               'Triumph', 'Ducati', 'BMW'],
});

/** Makes that build this body style. Never empty. */
export function makesFor(body: VehicleKindOfCar): readonly string[] {
  return BODY_MAKES[body];
}

const COLORS = [
  'white', 'black', 'silver', 'gray', 'blue', 'red', 'green', 'tan', 'maroon',
];

/**
 * Body styles, weighted the way a Wisconsin county actually looks. Mostly
 * trucks and crossovers, and motorcycles are rare, which is exactly why the
 * Aurora mismatch should have been obvious to anybody who looked.
 */
const BODIES: readonly { body: VehicleKindOfCar; weight: number }[] = Object.freeze([
  { body: 'suv', weight: 38 },
  { body: 'car', weight: 30 },
  { body: 'pickup', weight: 22 },
  { body: 'van', weight: 8 },
  { body: 'motorcycle', weight: 2 },
]);

const BODY_WEIGHTS = BODIES.map((b) => b.weight);

/**
 * What the car wearing this plate looks like.
 *
 * **Derived from the plate rather than rolled independently**, and that is
 * load-bearing rather than tidy. A registration entry describes the vehicle a
 * plate was issued to, so if the description were an independent roll then an
 * entry filed about *this very car* would still name a different make and body
 * about 98% of the time -- and it did, which made every single alert report a
 * vehicle mismatch. A field that is wrong on every stop tells a player nothing,
 * and the Aurora mechanic depends entirely on the field being right until the
 * one time it is not.
 *
 * So the plate identifies the vehicle, here and in the registration data, and a
 * mismatch means what it should: **this is not the car that plate belongs to.**
 */
export function describeVehicle(plate: string): Description {
  const at = (salt: string): number => descriptionHash(`${plate}:${salt}`) / 0x100000000;

  // Body first. The make has to be one that builds it.
  let roll = at('body') * BODY_WEIGHTS.reduce((sum, w) => sum + w, 0);
  let body: VehicleKindOfCar = 'car';
  for (const entry of BODIES) {
    roll -= entry.weight;
    if (roll <= 0) {
      body = entry.body;
      break;
    }
  }

  const makes = makesFor(body);
  return {
    make: makes[Math.floor(at('make') * makes.length)] as string,
    color: COLORS[Math.floor(at('color') * COLORS.length)] as string,
    body,
  };
}

/**
 * FNV-1a, the same hash the nationwide list uses.
 *
 * Shared deliberately: the registration description and the list entry have to
 * agree about what car a plate belongs to, and the cheapest way to guarantee
 * that is for both to be the same function of the same string.
 */
function descriptionHash(text: string): number {
  let value = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    value ^= text.charCodeAt(i);
    value = Math.imul(value, 0x01000193) >>> 0;
  }
  return value >>> 0;
}

/** "a white Ford pickup", for a line somebody reads off a screen. */
export function describeInWords(description: Description): string {
  return `${description.color} ${description.make} ${description.body}`;
}
